import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import type { Deck } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, Card, Input } from '../../components'
import { showToast } from '../../components/Toast'
import { createDeck, listMyDecks } from '../../lib/decks.js'
import styles from './DecksPage.module.css'

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Unknown' : DATE_FORMATTER.format(date)
}

function visibilityLabel(visibility: Deck['visibility']) {
  if (visibility === 'class') return 'Class'
  if (visibility === 'domain') return 'Domain'
  return 'Private'
}

export default function DecksPage(): JSX.Element {
  const { uid } = useAuth()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [decks, setDecks] = useState<Deck[] | null>(null)
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  const loadDecks = useCallback(async () => {
    if (!uid) return
    setLoading(true)
    setErrorMessage(null)

    try {
      setDecks(await listMyDecks(uid))
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load decks right now.'
      setErrorMessage(message)
      showToast(message, 'error')
    } finally {
      setLoading(false)
    }
  }, [uid])

  useEffect(() => {
    void loadDecks()
  }, [loadDecks])

  const filteredDecks = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) return decks ?? []
    return (decks ?? []).filter((deck) => deck.title.toLowerCase().includes(query))
  }, [decks, search])

  const summary = useMemo(() => {
    if (loading) return 'Loading your decks.'
    if (!decks || decks.length === 0) return 'No decks yet.'
    if (filteredDecks.length === 0) return 'No decks match your search.'
    return `${filteredDecks.length} deck${filteredDecks.length === 1 ? '' : 's'} shown.`
  }, [decks, filteredDecks.length, loading])

  async function handleCreateDeck() {
    if (!uid) return
    setCreating(true)
    try {
      const deck = await createDeck(
        {
          title: 'Untitled deck',
          description: '',
          subject: null,
          visibility: 'private',
          classId: null,
          tags: [],
        },
        uid,
      )
      showToast('New deck created.', 'success')
      navigate(`/teacher/decks/${deck.id}`)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to create a deck right now.'
      showToast(message, 'error')
    } finally {
      setCreating(false)
    }
  }

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Phase 3</p>
          <h2>My decks</h2>
        </div>
        <Button
          type="button"
          variant="primary"
          loading={creating}
          onClick={() => void handleCreateDeck()}
        >
          New deck
        </Button>
      </div>

      <Card className={styles.filters} elevated>
        <Input
          label="Search decks"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search by title"
        />
      </Card>

      <p role="status" aria-live="polite" className="sr-only">
        {summary}
      </p>

      <Card className={styles.tableCard} elevated>
        <div className={styles.tableHeader}>
          <div>
            <h3>Deck library</h3>
            <p className={styles.muted}>{summary}</p>
          </div>
        </div>

        {errorMessage && !loading ? (
          <div className={styles.stateBlock}>
            <p role="alert">{errorMessage}</p>
            <Button type="button" variant="secondary" onClick={() => void loadDecks()}>
              Retry
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className={styles.stateBlock}>
            <p role="status" aria-live="polite">
              Loading your decks…
            </p>
          </div>
        ) : null}

        {!loading && !errorMessage && decks?.length === 0 ? (
          <div className={styles.emptyState}>
            <p>No decks yet. Create your first deck to get started.</p>
            <Button
              type="button"
              variant="primary"
              loading={creating}
              onClick={() => void handleCreateDeck()}
            >
              New deck
            </Button>
          </div>
        ) : null}

        {!loading && !errorMessage && decks && decks.length > 0 ? (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <caption>Your decks</caption>
              <thead>
                <tr>
                  <th scope="col">Title</th>
                  <th scope="col">Subject</th>
                  <th scope="col">Visibility</th>
                  <th scope="col">Cards</th>
                  <th scope="col">Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredDecks.map((deck) => (
                  <tr key={deck.id}>
                    <th scope="row">
                      <Link className={styles.deckLink} to={`/teacher/decks/${deck.id}`}>
                        {deck.title || 'Untitled deck'}
                      </Link>
                      {deck.description ? (
                        <span className={styles.description}>{deck.description}</span>
                      ) : null}
                    </th>
                    <td>{deck.subject ?? 'Not set'}</td>
                    <td>
                      <span className={styles.badge}>{visibilityLabel(deck.visibility)}</span>
                    </td>
                    <td>{deck.cardCount}</td>
                    <td>{formatDate(deck.updatedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </section>
  )
}
